import React, { useState, useEffect, useCallback } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { FundsRawExplorerProps, FundRawRecord } from "./types";
import FundsRawFilters from "./FundsRawFilters";
import FundsRawTable from "./FundsRawTable";
import { useToast } from "@/components/ui/use-toast";
import { ExploreBulkActionsToolbar } from "@/components/explore/ExploreBulkActionsToolbar";
import { useAddToPortfolio } from "@/hooks/portfolio/useAddToPortfolio";

/**
 * FundsRawExplorer Component
 * 
 * Container component that manages filters state for the Funds Raw Explorer
 * - Manages search text and filter states
 * - Passes filter props to table component
 * - Table component handles all data fetching via React Query
 */
export function FundsRawExplorer({ 
  initialSearchText = "",
  initialCategory = "",
  initialFundType = null,
}: FundsRawExplorerProps) {
  const { toast } = useToast();

  // Filter states
  const [searchText, setSearchText] = useState<string>("");
  const [selectedFundType, setSelectedFundType] = useState<string | null>(null);
  const [selectedSubAssetClass, setSelectedSubAssetClass] = useState<string | null>(null);
  
  // Use debounced search to reduce API calls
  const debouncedSearchText = useDebounce(searchText, 500);
  
  // Count state for filters display
  const [totalCount, setTotalCount] = useState<number>(0);
  const [visibleCount, setVisibleCount] = useState<number>(0);
  
  // Provider-based portfolio dialog (new provider)
  const { openFundsRaw } = useAddToPortfolio();
  
  // Callback to get selected funds from table
  const [getSelectedFundsData, setGetSelectedFundsData] = useState<(() => FundRawRecord[]) | null>(null);
  

  
  // Bulk selection state
  const [bulkSelectionState, bulkSelectionActions] = useBulkSelection(totalCount);
  
  // Handler for updating counts in filter display
  const handleCountsUpdate = (counts: { total: number; filtered: number; visible: number }) => {
    setTotalCount(counts.total);
    setVisibleCount(counts.visible);
  };

  // Handle favorite button click
  const handleFavorite = (fundId: string) => {
    console.log("Add to favorites:", fundId);
    // TODO: Implement favorite functionality
  };

  // Handle add to watchlist button click
  const handleAddToWatchlist = (fundId: string) => {
    console.log("Add to watchlist:", fundId);
    // TODO: Implement watchlist functionality
  };

  // Handle add to portfolio button click
  const handleAddToPortfolio = (fundId: string, fund: FundRawRecord) => {
    console.log("Add to portfolio:", fundId);
    openFundsRaw({ fund, onSuccess: () => {
      toast({ title: "Success", description: "Fund added to portfolio successfully" });
    }});
  };
  
  // Debounce state to prevent multiple calls
  const [isProcessing, setIsProcessing] = useState(false);

  // Bulk action handlers
  const handleBulkAddToPortfolio = useCallback(async () => {
    if (isProcessing) return;
    
    const selectedIds = Array.from(bulkSelectionState.selectedIds);
    if (selectedIds.length === 0) {
      toast({
        title: "No Selection",
        description: "Please select funds to add to portfolio",
        variant: "destructive",
      });
      return;
    }
    
    // Get the actual selected fund data from the callback if available
    if (!getSelectedFundsData || typeof getSelectedFundsData !== 'function') {
      toast({
        title: "Loading",
        description: "Please wait for the table to load before selecting funds",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const selectedFundData = getSelectedFundsData();
      openFundsRaw({ funds: selectedFundData, onSuccess: () => {
        toast({ title: "Success", description: `${selectedFundData.length} funds added to portfolio successfully` });
        bulkSelectionActions.clearSelection();
      }});
    } catch (error) {
      console.error('Error getting selected funds:', error);
      toast({
        title: "Error",
        description: "Failed to get selected funds data",
        variant: "destructive",
      });
    }
  }, [bulkSelectionState.selectedIds, isProcessing, getSelectedFundsData]);
  
  const handleBulkFavorite = () => {
    console.log("Bulk favorite:", Array.from(bulkSelectionState.selectedIds));
    // TODO: Implement bulk favorite
  };
  
  const handleBulkExport = () => {
    console.log("Bulk export:", Array.from(bulkSelectionState.selectedIds));
    // TODO: Implement bulk export
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <FundsRawFilters
        searchText={searchText}
        onSearchChange={setSearchText}
        selectedFundType={selectedFundType}
        selectedSubAssetClass={selectedSubAssetClass}
        onFundTypeFilterChange={setSelectedFundType}
        onSubAssetClassFilterChange={setSelectedSubAssetClass}
        totalCount={totalCount}
        visibleCount={visibleCount}
      />
      
      {/* Bulk Actions Toolbar */}
      <ExploreBulkActionsToolbar
        selectedCount={bulkSelectionState.selectedCount}
        totalCount={totalCount}
        onClearSelection={bulkSelectionActions.clearSelection}
        onAddToPortfolio={handleBulkAddToPortfolio}
        onFavorite={handleBulkFavorite}
        onExport={handleBulkExport}
      />
      
      {/* Table - now with filter props directly passed */}
      <FundsRawTable
        filters={{
          searchText: debouncedSearchText,
          fundType: selectedFundType,
          subAssetClass: selectedSubAssetClass,
          category: undefined
        }}
        onAddToPortfolio={handleAddToPortfolio}
        onFavorite={handleFavorite}
        onAddToWatchlist={handleAddToWatchlist}
        onCountsUpdate={handleCountsUpdate}
        queryEnabled={true}
        bulkSelection={{
          isSelected: bulkSelectionActions.isSelected,
          toggleItem: bulkSelectionActions.toggleItem,
          toggleAll: bulkSelectionActions.toggleAll,
          isAllSelected: bulkSelectionState.isAllSelected,
          isIndeterminate: bulkSelectionState.isIndeterminate,
        }}
        onSetGetSelectedData={(callback) => setGetSelectedFundsData(() => callback)}
      />
      
      {/* Portfolio dialog is provided globally by FundsPortfolioDialogProvider */}
    </div>
  );
}

export default FundsRawExplorer;
